<template>
  <v-layout align-start>
    <v-flex>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Asignación de Roles</v-toolbar-title>
        <v-divider class="mx-2" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Buscar usuario"
          single-line
          hide-details
        ></v-text-field>
      </v-toolbar>

      <v-container fluid>
        <!-- Resumen de roles -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3" v-for="(stat, index) in roleStats" :key="index">
            <v-card class="pa-3" :color="stat.color" dark>
              <v-card-title class="text-subtitle-1 pb-0">{{ stat.label }}</v-card-title>
              <v-card-text class="text-h5 pt-1">{{ stat.count }}</v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filtro por rol -->
        <v-row class="mb-2">
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filterRole"
              :items="roleFilterOptions"
              label="Filtrar por rol"
              clearable
              dense
              outlined
            ></v-select>
          </v-col>
        </v-row>

        <!-- Tabla de usuarios -->
        <v-data-table
          :headers="headers"
          :items="filteredUsers"
          :search="search"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:item.rol="{ item }">
            <v-chip
              v-for="role in parseRoles(item.rol)"
              :key="role"
              :color="getRoleColor(role)"
              dark
              small
              class="mr-1 mb-1"
            >
              {{ role }}
            </v-chip>
          </template>

          <template v-slot:item.active="{ item }">
            <v-icon :color="item.active ? 'success' : 'error'" small>
              {{ item.active ? 'check_circle' : 'cancel' }}
            </v-icon>
          </template>

          <template v-slot:item.asignar="{ item }">
            <v-btn small color="primary" outlined @click="openRolesDialog(item)">
              <v-icon left small>edit</v-icon> Editar roles
            </v-btn>
          </template>

          <template v-slot:no-data>
            <v-btn color="primary" @click="loadUsers()">Recargar</v-btn>
          </template>
        </v-data-table>
      </v-container>

      <!-- Modal asignar roles con checkboxes -->
      <v-dialog v-model="rolesDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline primary white--text">
            Roles de {{ pendingChange.userName }}
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="mb-3">Selecciona los roles que deseas asignar:</p>
            <v-checkbox
              v-for="role in availableRoles"
              :key="role"
              v-model="selectedRoles"
              :label="role"
              :value="role"
              :color="getRoleColor(role)"
              hide-details
              dense
              class="mb-1"
            ></v-checkbox>
            <v-alert v-if="selectedRoles.length === 0" type="warning" dense class="mt-3">
              Debes seleccionar al menos un rol.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="rolesDialog = false">Cancelar</v-btn>
            <v-btn
              color="primary"
              @click="confirmRolesChange"
              :disabled="selectedRoles.length === 0"
            >
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";
import swal from "sweetalert";

export default {
  name: "RoleAssignment",
  data() {
    return {
      search: "",
      loading: false,
      filterRole: null,
      allUsers: [],
      rolesDialog: false,
      selectedRoles: [],
      pendingChange: {
        userId: "",
        userName: "",
      },
      availableRoles: [
        "Super Admin",
        "Administrador",
        "Finanzas",
        "Editor de Blog",
        "Embajador",
        "Affiliate",
      ],
      headers: [
        { text: "Nombre", value: "name", sortable: true },
        { text: "Apellido", value: "surName", sortable: true },
        { text: "Email", value: "email", sortable: true },
        { text: "Roles", value: "rol", sortable: false },
        { text: "Estado", value: "active", sortable: true },
        { text: "Acciones", value: "asignar", sortable: false, width: "180px" },
      ],
    };
  },
  created() {
    this.loadUsers();
  },
  computed: {
    ...mapState("userNamespace", ["token"]),
    filteredUsers() {
      if (!this.filterRole) return this.allUsers;
      return this.allUsers.filter((u) => {
        const roles = this.parseRoles(u.rol);
        return roles.includes(this.filterRole);
      });
    },
    roleFilterOptions() {
      const roles = new Set();
      this.allUsers.forEach((u) => {
        this.parseRoles(u.rol).forEach((r) => roles.add(r));
      });
      return [...roles].sort();
    },
    roleStats() {
      const counts = {};
      this.allUsers.forEach((u) => {
        this.parseRoles(u.rol).forEach((r) => {
          counts[r] = (counts[r] || 0) + 1;
        });
      });

      const colorMap = {
        "Super Admin": "red darken-2",
        "Administrador": "orange darken-2",
        "Finanzas": "indigo",
        "Editor de Blog": "deep-purple",
        "Embajador": "cyan darken-1",
        "Vendedor": "green darken-1",
        "Almacenero": "blue-grey",
        "Affiliate": "blue",
        "usuario": "grey darken-1",
      };

      return Object.entries(counts).map(([role, count]) => ({
        label: role,
        count,
        color: colorMap[role] || "grey",
      }));
    },
  },
  methods: {
    ...mapActions("userNamespace", ["getUsers", "updateUser"]),

    parseRoles(rol) {
      if (!rol) return ["usuario"];
      if (Array.isArray(rol)) return rol;
      try {
        const parsed = JSON.parse(rol);
        return Array.isArray(parsed) ? parsed : [rol];
      } catch {
        return [rol];
      }
    },

    async loadUsers() {
      this.loading = true;
      try {
        await this.getUsers();
        this.allUsers = this.$store.state.userNamespace.users || [];
      } catch (error) {
        console.error("Error loading users:", error);
      } finally {
        this.loading = false;
      }
    },

    getRoleColor(role) {
      const colors = {
        "Super Admin": "red darken-2",
        "Administrador": "orange darken-2",
        "Finanzas": "indigo",
        "Editor de Blog": "deep-purple",
        "Embajador": "cyan darken-1",
        "Vendedor": "green darken-1",
        "Almacenero": "blue-grey",
        "Affiliate": "blue",
        "usuario": "grey darken-1",
      };
      return colors[role] || "grey";
    },

    openRolesDialog(user) {
      this.pendingChange = {
        userId: user._id,
        userName: `${user.name} ${user.surName}`,
      };
      this.selectedRoles = [...this.parseRoles(user.rol)];
      this.rolesDialog = true;
    },

    async confirmRolesChange() {
      if (this.selectedRoles.length === 0) return;
      try {
        await this.updateUser({
          id: this.pendingChange.userId,
          rol: JSON.stringify(this.selectedRoles),
        });
        this.rolesDialog = false;
        await this.loadUsers();
        swal({
          title: "Roles actualizados",
          text: `${this.pendingChange.userName} ahora tiene: ${this.selectedRoles.join(", ")}`,
          icon: "success",
        });
      } catch (error) {
        swal({
          title: "Error",
          text: "No se pudieron cambiar los roles",
          icon: "error",
        });
      }
    },
  },
};
</script>

<style scoped>
.v-data-table {
  margin-top: 8px;
}
</style>
